/**
 * Rollbar configuration for Marketplace
 * Provides error tracking and monitoring in production
 */

// Browser extensions (translation tools, ad blockers, password managers) inject
// postMessage/DOM hooks into third-party pages and occasionally throw this exact
// message when their own injected script goes stale mid-page-lifecycle. It's not
// triggered by our code and isn't actionable, so keep it out of Rollbar.
const IGNORED_MESSAGE_PATTERNS = [
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/
];

// Errors thrown from inside third-party scripts we don't control (seen from bot
// traffic running patched headless browsers). Matched against the throw site only,
// so our own errors that merely pass through these scripts are still reported.
const IGNORED_ORIGIN_PATTERN = /^https?:\/\/maps\.googleapis\.com\//;

// Cross-origin errors the browser masks to this exact message with no stack.
const MASKED_SCRIPT_ERROR_PATTERN = /^Script error\.?$/;

// Rollbar passes the item as the 3rd arg; its body lives at item.data.body.
function getBody(payload) {
  return payload?.data?.body || payload?.body || {};
}

function getFrames(payload) {
  const body = getBody(payload);
  const traces = body.trace ? [body.trace] : body.trace_chain || [];
  return traces.flatMap(trace => (Array.isArray(trace?.frames) ? trace.frames : []));
}

function firstStackUrl(stack) {
  const match = typeof stack === 'string' ? stack.match(/https?:\/\/[^\s)]+/) : null;
  return match ? match[0] : '';
}

function isIgnoredOrigin(argList, payload) {
  const errorUrls = argList
    .filter(arg => arg && typeof arg === 'object')
    .flatMap(arg => [arg.fileName, arg.filename, firstStackUrl(arg.stack)]);
  // Rollbar frames are ordered outermost-first; the last frame is the throw site.
  const frames = getFrames(payload);
  const throwSite = frames.length ? frames[frames.length - 1]?.filename : '';
  return [...errorUrls, throwSite].some(
    url => typeof url === 'string' && IGNORED_ORIGIN_PATTERN.test(url)
  );
}

function checkIgnore(_isUncaught, args, payload) {
  // args is not always a real array (e.g. an `arguments` object for uncaught errors).
  const argList = Array.isArray(args)
    ? args
    : args && typeof args.length === 'number'
      ? Array.prototype.slice.call(args)
      : [];
  const body = getBody(payload);
  const message = String(
    argList[0]?.message ||
      (typeof argList[0] === 'string' ? argList[0] : null) ||
      body.trace?.exception?.message ||
      body.message?.body ||
      ''
  );

  if (IGNORED_MESSAGE_PATTERNS.some(pattern => pattern.test(message))) {
    return true;
  }
  if (
    MASKED_SCRIPT_ERROR_PATTERN.test(message.trim()) &&
    !getFrames(payload).some(frame => frame?.filename)
  ) {
    return true;
  }
  return isIgnoredOrigin(argList, payload);
}

const rollbarConfig = {
  accessToken: '0ae64d76bdb3455e9f97fd3e586be182',
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: process.env.NODE_ENV === 'production',
  environment: process.env.NODE_ENV === 'production' ? 'marketplace-production' : 'marketplace-development',
  checkIgnore,
  payload: {
    client: {
      javascript: {
        code_version: '1.0.0',
        source_map_enabled: true,
        guess_uncaught_frames: true
      }
    }
  }
};

export default rollbarConfig;
